/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState } from 'react';
import { StandardProps } from '@/newcar/ui/contract';
import QAModal from '@/newcar/ui/contract/organism/QAModal';
import * as styles from './index.module.styl';

type TypographyType = 'title' | 'subtitle1' | 'caption';
type IconType = 'none' | 'required' | 'elective';

const LabelRequired = (): JSX.Element => (
  <span className={`${styles.required}`} />
);

const LabelElective = (): JSX.Element => (
  <span className={`${styles.elective} cfs-10 position-relative top-np2`}>任意</span>
);

// #region declare props
interface BaseTypographyProps
  extends StandardProps {
  /**
   * @requires
   */
  value: string | React.ReactNode;

  /**
   * @default body
   */
  type?: TypographyType

  /**
   * @default none
   */
  icon?: IconType

  /**
   * @ignore
   */
  children?: never;

  /**
   * @default true
   */
  visible?: boolean;

  /**
   * @default ''
   */
  adornmentStart?: string;

  /**
   * @default ''
   */
  balloon?: string;

  /**
   * @default []
   */
  qa?: {q: string, a: string}[];
}
// #endregion

const iconTypeElements: { [Key in IconType]: React.ReactElement } = {
  none: <></>,
  required: <LabelRequired />,
  elective: <LabelElective />,
};

export declare type TypographyProps = BaseTypographyProps;
/**
 * 注意書きや注釈用のコンポーネント
 *
 * @export
 * @param {TypographyProps} props
 * @return {*}  {JSX.Element}
 */
export default function Typography(props: TypographyProps): JSX.Element {
  const {
    className = '',
    icon = 'none',
    value,
    type = 'body',
    adornmentStart = '',
    balloon = '',
    qa = [],
  } = props;

  // 現住所OA用
  const [isShowQA, setShowQA] = useState<boolean>(false);

  // QAを表示する
  const handleClickQA = () => {
    setShowQA(true);
  };

  // QAを閉じる
  const handleCloseQA = () => {
    setShowQA(false);
  };

  switch (type) {
    case 'caption':
      return (
        <div
          className={`is-size-4 is-relative ${className}`}
          style={{ whiteSpace: 'pre-wrap' }}
        >
          {adornmentStart}
          {iconTypeElements[icon]}
          {value}
        </div>
      );
    case 'title':
      return (
        <div className="block is-margin-bottom-5">
          <h2 className={`title is-2 is-padding-top-5 ${className}`}>
            {iconTypeElements[icon]}
            {value}
          </h2>
        </div>
      );
    case 'subtitle1':
      return (
        <div className={`is-mobile has-text-weight-bold is-size-3 ${styles.subheading} ${styles.bgYellow} ${className}`}>
          <div className="columns is-vcentered is-mobile has-text-weight-bold ">
            <div className={`${styles.headingLine}`} />
            <div className={`column is-narrow has-text-centered ${styles.requiredIcon}`}>必須</div>
            <div className="column is-narrow is-size-3">{value}</div>
            { balloon !== ''
              ? (
                <div className={`is-margin-left-3 ${styles.inductionTip}`}>
                  <p className={`column has-text-weight-bold ${styles.comment}`}>
                    {balloon}
                  </p>
                </div>
              ) : <></>}
            { qa.length > 0
              ? (
                <>
                  <div className="column">
                    <i
                      className={styles.iconHelp}
                      data-gtm="gtm-newcarform-currentadressSectionQA"
                      role="button"
                      tabIndex={-1}
                      onClick={() => handleClickQA()}
                    />
                  </div>
                  <QAModal
                    qa={qa}
                    isActive={isShowQA}
                    handleCloseModal={handleCloseQA}
                  />
                </>
              ) : <></>}
          </div>
        </div>
      );
    default:
      return <></>;
  }
}
